import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Shield,
  Heart,
  Users,
  Target,
  RefreshCw,
  Sparkles,
  CheckCircle,
  ArrowRight,
  Phone,
  Handshake,
  Home,
  GraduationCap,
  TrendingUp,
  Clock,
  UserCheck,
  FileText,
  MessageSquare,
  Smartphone,
} from "lucide-react";

const epicFramework = [
  {
    letter: "E",
    title: "Empowerment",
    description:
      "Young people are involved in decisions, goal setting and independence building.",
    icon: Sparkles,
    color: "bg-amber-500",
  },
  {
    letter: "P",
    title: "Protection",
    description:
      "Safeguarding and emotional wellbeing are central to all support.",
    icon: Shield,
    color: "bg-teal-500",
  },
  {
    letter: "I",
    title: "Individualised Support",
    description:
      "Each young person has a tailored support plan based on needs and strengths.",
    icon: Target,
    color: "bg-blue-500",
  },
  {
    letter: "C",
    title: "Consistency",
    description: "Reliable staff relationships and structured routines.",
    icon: Clock,
    color: "bg-indigo-500",
  },
  {
    letter: "C",
    title: "Collaboration",
    description:
      "Working with social workers, education, families and professionals.",
    icon: Handshake,
    color: "bg-purple-500",
  },
  {
    letter: "A",
    title: "Aspirations",
    description: "Education, employment and positive future pathways.",
    icon: GraduationCap,
    color: "bg-pink-500",
  },
  {
    letter: "R",
    title: "Responsive Care",
    description:
      "Flexible support responding to risks, behaviours and emotional needs.",
    icon: RefreshCw,
    color: "bg-rose-500",
  },
];

const referralSteps = [
  {
    step: "01",
    title: "Referral Enquiry",
    description: "Submit your referral through our secure online form",
  },
  {
    step: "02",
    title: "Suitability Assessment",
    description: "We discuss risks and support requirements",
  },
  {
    step: "03",
    title: "Matching & Planning",
    description: "Finding the right placement and planning transition",
  },
  {
    step: "04",
    title: "Support & Reviews",
    description: "Ongoing support plan and regular progress reviews",
  },
];

const whyChooseCards = [
  {
    icon: Shield,
    title: "Safeguarding First Approach",
    description:
      "Safeguarding underpins every aspect of our support. Staff are trained, supervised and work in partnership with professionals to manage risk and promote safety.",
  },
  {
    icon: UserCheck,
    title: "Consistent Relationships",
    description:
      "We prioritise stable staffing and regular keywork sessions so young people can build trust and feel secure.",
  },
  {
    icon: FileText,
    title: "Individual Support Planning",
    description:
      "Each young person has a tailored support plan focusing on emotional wellbeing, routines, education and independence skills.",
  },
  {
    icon: MessageSquare,
    title: "Multi-Agency Working",
    description:
      "We communicate regularly with social workers, education providers and external professionals to ensure coordinated support.",
  },
  {
    icon: Heart,
    title: "Focus on Engagement",
    description:
      "We aim to re-engage young people who may struggle with placements by building positive relationships and achievable goals.",
  },
  {
    icon: Smartphone,
    title: "Enhanced Support Through Digital Tools",
    description:
      "Epic Care also uses Pocket Keyworker — a digital engagement platform that helps young people communicate with staff, access life-skills guidance and support progress tracking.",
  },
];

const supportGroups = [
  "At risk of exploitation",
  "Experiencing placement breakdown",
  "Preparing for independence",
  "Needing structured support",
];

const outcomes = [
  {
    icon: TrendingUp,
    title: "Improved Engagement",
    description: "Supporting young people to engage positively with services",
  },
  {
    icon: Home,
    title: "Routine & Stability",
    description: "Building consistent daily structures and safe environments",
  },
  {
    icon: GraduationCap,
    title: "Education Pathways",
    description: "Access to education, training and skills development",
  },
  {
    icon: Shield,
    title: "Risk Reduction",
    description: "Working to reduce risky behaviours through support",
  },
];

export default function HomePage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        {/* Decorative blobs */}
        <div className="blob-teal -top-40 -right-40" />
        <div className="blob-amber top-1/2 -left-20" />

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                Trauma-informed youth support
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6"
                data-testid="hero-headline"
              >
                Safe, supportive pathways for{" "}
                <span className="text-teal-600">young people</span>
              </h1>

              <p
                className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl"
                data-testid="hero-subheadline"
              >
                Epic Care provides structured support to help young people
                develop stability, independence and positive futures.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/referrals">
                  <Button
                    size="lg"
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 h-14 text-lg btn-hover"
                    data-testid="hero-referral-btn"
                  >
                    Make a Referral
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full px-8 h-14 text-lg border-slate-200 hover:bg-slate-50"
                    data-testid="hero-partner-btn"
                  >
                    Partner With Us
                  </Button>
                </Link>
              </div>

              {/* Trust Bar */}
              <div
                className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500"
                data-testid="trust-bar"
              >
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500" />
                  Trauma-informed
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500" />
                  Safeguarding-led
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500" />
                  Outcome-focused
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500" />
                  Partnership-driven
                </span>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1763731093228-e17859c7a63e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Two people walking together along a tree-lined pathway"
                  className="w-full h-[500px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Empowerment & Protection</p>
                    <p className="text-sm text-slate-500">Central to everything we do</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Support */}
      <section className="section-padding bg-white" data-testid="who-we-support-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Who We Support
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                We work with young people aged 16-25 who may need additional
                support to build positive futures. Our trauma-informed approach
                ensures each young person receives tailored care.
              </p>
              <div className="space-y-4">
                {supportGroups.map((group, index) => (
                  <motion.div
                    key={group}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{group}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1618173887111-3ecfc91c41b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Young people sitting together on a bench outdoors in a supportive setting"
                className="rounded-3xl shadow-lg w-full h-[400px] object-cover"
                data-testid="support-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* EPIC CARE Framework */}
      <section
        className="section-padding bg-slate-50"
        data-testid="epic-framework-section"
      >
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              The EPIC CARE Framework
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
              Empowerment and Protection is Central to everything we do
            </p>
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-6 py-3 rounded-full font-medium">
              <Heart className="w-5 h-5" />
              "At Epic Care, Empowerment and Protection are central to everything we do."
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {epicFramework.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 card-hover"
                data-testid={`epic-card-${item.letter.toLowerCase()}-${index}`}
              >
                <div
                  className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center text-white font-bold text-xl mb-4`}
                >
                  {item.letter}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Epic Care - For Local Authorities */}
      <section
        className="section-padding bg-gradient-to-b from-teal-50/50 to-white"
        data-testid="why-choose-section"
      >
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Why Local Authorities Work With Epic Care
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Epic Care provides structured, relationship-based support for young people.
              Our approach combines safeguarding, consistency and engagement to help young
              people stabilise and work towards independence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {whyChooseCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300"
                data-testid={`why-choose-card-${index}`}
              >
                <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-5">
                  <card.icon className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="font-semibold text-slate-900 text-lg mb-3">
                  {card.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {card.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* CTA Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-slate-900 rounded-3xl p-8 md:p-12 text-center"
            data-testid="why-choose-cta"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Need to discuss a placement or suitability?
            </h3>
            <div className="flex flex-col items-center gap-4">
              <Link to="/referrals">
                <Button
                  size="lg"
                  className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 h-14 text-lg"
                  data-testid="why-choose-referral-btn"
                >
                  Make a Referral Enquiry
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <p className="text-slate-400 text-sm">
                We welcome informal discussions prior to referral.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How Referrals Work */}
      <section className="section-padding bg-white" data-testid="referral-steps-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How Referrals Work
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A simple, secure process to discuss placement needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {referralSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                {index < referralSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-full h-0.5 bg-slate-200" />
                )}
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center text-white font-bold text-xl mb-4">
                    {step.step}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/referrals">
              <Button
                size="lg"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8"
                data-testid="start-referral-btn"
              >
                Start a Referral
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section-padding bg-slate-900 text-white" data-testid="outcomes-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Supporting Positive Outcomes
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Epic Care aims to support young people in achieving meaningful progress
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={outcome.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <outcome.icon className="w-8 h-8 text-teal-400" />
                </div>
                <h3 className="font-semibold mb-2">{outcome.title}</h3>
                <p className="text-slate-400 text-sm">{outcome.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need to discuss a placement?
          </h2>
          <p className="text-teal-100 text-lg mb-8 max-w-xl mx-auto">
            Our team is here to help. Get in touch to discuss referrals or
            partnership opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/referrals">
              <Button
                size="lg"
                className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8 h-14"
                data-testid="cta-referral-btn"
              >
                Make a Referral
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-8 h-14"
                data-testid="cta-contact-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
